import { useState } from 'react';
import { Offcanvas, Row } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import FormSelectMethod from './FormSelectMethod';
import { ContactMethod } from 'components/Notifications/recipientTypes';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import {
  useSendNotification,
  SendNotificationFormFields,
} from 'hooks/useNotification';
import { useDistributionLists } from 'hooks/useDistributionLists';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';

type AlertDrawerProps = {
  toggle: () => void;
};

const AlertDrawer: React.FC<AlertDrawerProps> = ({ toggle }) => {
  const [selectedRecipientType, setSelectedRecipientType] =
    useState<ContactMethod>('Email');

  const mutation = useSendNotification();
  const groupId = useGroupGuid();

  // Fetch incident members
  const { data: rosterData = [] } = useGetRostersForGroup(groupId);
  const { data: distributionListItems = [] } = useDistributionLists(groupId);

  const onAlertDrawerSubmit = async (data: SendNotificationFormFields) => {
    await mutation.mutateAsync(data);
    toggle();
  };

  const form = useForm<SendNotificationFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      sendType: 'now',
    },
  });

  const { handleSubmit, watch } = form;

  const method = watch('contact_method');
  const unifiedMessageChoice = watch('unified_message_choice');

  // Automatically select the method if there is only one
  // if (method?.length === 1 && selectedRecipientType !== method[0]) {
  //   setSelectedRecipientType(method[0]);
  // } else if (method?.length === 0 && selectedRecipientType !== null) {
  //   setSelectedRecipientType(null);
  // }

  return (
    <DrawerWrapper toggle={toggle} title="Schedule Alert">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onAlertDrawerSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <div>
                Communication Channels
                <span aria-label="Required field" className="required">
                  *
                </span>
              </div>
              <FormSelectMethod
                fieldName="contact_method"
                recipientTypeOptions={['Email', 'SMS']}
                rules={{
                  required: {
                    value: true,
                    message: 'Select at least one communication channel',
                  },
                }}
              />
            </Row>
            <Row>
              <div>
                Alert Message
                <span aria-label="Required field" className="required">
                  *
                </span>
              </div>
            </Row>
            <Row>
              <FormTextField
                title="Email Subject"
                fieldName="subject"
                rules={{
                  required: {
                    value: method?.includes('Email'),
                    message: 'This field is required',
                  },
                  maxLength: {
                    value: 100,
                    message: 'The maximum length is 100 characters',
                  },
                }}
                disabled={!method?.includes('Email')}
                variant="half"
              />
            </Row>
            {method?.length > 1 && (
              <>
                <StylishNewToggleButtonGroup
                  data={method.map((m) => ({ label: m, value: m }))}
                  className="w-25"
                  selected={selectedRecipientType}
                  onChange={(value) =>
                    setSelectedRecipientType(value as ContactMethod)
                  }
                  disabled={unifiedMessageChoice}
                />
                <FormCheckboxField
                  title="Keep message the same across all channels"
                  fieldName="unified_message_choice"
                />
              </>
            )}
            <Row>
              {unifiedMessageChoice && method?.length > 0 && (
                <FormTextAreaField
                  title="Message"
                  fieldName="unified_message"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 160,
                      message: 'The maximum length is 160 characters',
                    },
                  }}
                  variant="full"
                />
              )}
              {selectedRecipientType === 'Email' && !unifiedMessageChoice && (
                <FormTextAreaField
                  title="Message"
                  fieldName="email_message"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 300,
                      message: 'The maximum length is 300 characters',
                    },
                  }}
                  variant="full"
                />
              )}
              {selectedRecipientType === 'SMS' && !unifiedMessageChoice && (
                <FormTextAreaField
                  title="Message"
                  fieldName="sms_message"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 160,
                      message: 'The maximum length is 160 characters',
                    },
                  }}
                  variant="full"
                />
              )}
            </Row>
            <Row className="mb-3">
              <h6>
                Recipients
                <span aria-label="Required field" className="required">
                  *
                </span>
              </h6>
              <Controller
                name="individuals"
                control={form.control}
                render={({ field: { onChange, value } }) => (
                  <PratusSelect
                    label="Individuals"
                    isMulti
                    value={
                      value
                        ? rosterData
                            .filter((member) =>
                              value.includes(member.user_guid)
                            )
                            .map((member) => ({
                              label:
                                member.email_address || member.user_name || '',
                              value: member.user_guid,
                            }))
                        : []
                    }
                    options={rosterData.map((member) => ({
                      label: member.email_address || member.user_name || '',
                      value: member.user_guid,
                    }))}
                    onChange={(selected) => {
                      if (Array.isArray(selected)) {
                        onChange(selected.map((opt) => opt.value));
                      } else if (selected && 'value' in selected) {
                        onChange([selected.value]);
                      } else {
                        onChange([]);
                      }
                    }}
                  />
                )}
              />
            </Row>
            <Row className="mb-3">
              <Controller
                name="teams"
                control={form.control}
                render={({ field: { onChange, value } }) => (
                  <PratusSelect
                    label="Teams"
                    isMulti
                    value={
                      value
                        ? distributionListItems
                            .filter((list) => value.includes(list.id))
                            .map((list) => ({
                              label: list.name || '',
                              value: list.id,
                            }))
                        : []
                    }
                    options={distributionListItems.map((list) => ({
                      label: list.name || '',
                      value: list.id,
                    }))}
                    onChange={(selected) => {
                      if (Array.isArray(selected)) {
                        onChange(selected.map((opt) => opt.value));
                      } else if (selected && 'value' in selected) {
                        onChange([selected.value]);
                      } else {
                        onChange([]);
                      }
                    }}
                  />
                )}
              />
            </Row>
            <Row className="mb-3">
              <Controller
                name="sendType"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <StylishNewToggleButtonGroup
                    data={[
                      { label: 'Send Alert Now', value: 'now' },
                      { label: 'Schedule Alert', value: 'schedule' },
                    ]}
                    selected={value}
                    onChange={onChange}
                  />
                )}
              />
            </Row>
            {watch('sendType') === 'schedule' && (
              <Controller
                name="scheduledDate"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <StylishDateTimePicker
                    value={value || null}
                    type={'datetime-local'}
                    minDate={new Date()}
                    onChange={onChange}
                    dateLabel={'Start Date'}
                    timeLabel={'Start Time'}
                  />
                )}
              />
            )}
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton className="button--primary" type="submit">
                Send
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default AlertDrawer;
